import gsap from 'gsap'
import { once } from 'ramda'
import type { PerspectiveCamera } from 'three'
import { Vector2, Vector3 } from 'three'
import { CAMERA_DISTANCE_TO_TARGET } from '@/lib/site-viewer/constants'
import { uvArcDistance, uvOpposite, uvToWorld, worldToUv } from '@/lib/site-viewer/utils'
import type { Vec2Data } from '@/entities/model3d'

type CameraViewData = {
  fov: Vec2Data
  target: Vec2Data
}

const MIN_RANGE_DURATION = 1.5
const MAX_RANGE_DURATION = 3

type RangeDuration = { type: 'range'; min: number; max: number }
type RangeMinDuration = { type: 'range'; min: number }
type RangeMaxDuration = { type: 'range'; max: number }
type StaticDuration = { type: 'static'; length: number }
export type Duration = number | StaticDuration | RangeDuration | RangeMinDuration | RangeMaxDuration

// Returns a function to cancel the tween
const tweenCamera = (
  camera: PerspectiveCamera,
  destinationData: Partial<CameraViewData>,
  durationDefn: Duration = { type: 'range', min: MIN_RANGE_DURATION, max: MAX_RANGE_DURATION },
  onUpdate: (newPos: Vec2Data) => void = () => {},
  onComplete: () => void = () => {}
): (() => void) => {
  if (typeof durationDefn === 'number')
    return tweenCamera(camera, destinationData, { type: 'static', length: durationDefn }, onUpdate, onComplete)
  const min = 'min' in durationDefn ? durationDefn.min : MIN_RANGE_DURATION
  const max = 'max' in durationDefn ? durationDefn.max : MAX_RANGE_DURATION
  let duration: number = 'length' in durationDefn ? durationDefn.length : min

  let { fov, target } = destinationData
  const completeOnlyOnce = once(onComplete)

  let positionTween: gsap.core.Tween | null = null

  if (target) {
    const uvCoords = uvOpposite(target)
    const to = uvCoords
    const from = new Vector2().copy(worldToUv(new Vector3().copy(camera.position as Vector3).normalize()) as Vector2)
    const diff = new Vector2(to.x, to.y).sub(from)

    // Since UV coords have an x-range of 0 < x < 1, find if there is a shorter path by wrapping around
    if (Math.abs(diff.x) > Math.abs(diff.x - 1)) {
      diff.x -= 1
    }
    if (Math.abs(diff.x) > Math.abs(diff.x + 1)) {
      diff.x += 1
    }
    const progress = { x: 0 }

    if (durationDefn.type === 'range') {
      const curveLength = uvArcDistance(from, diff)

      // scale by pi (approximately the worst possible case)
      duration = (curveLength * (max - min)) / Math.PI + min
    }

    positionTween = gsap.to(progress, {
      duration,
      ease: 'power1',
      x: 1,
      // Notify caller of an update. Only has to happen in one of the tweens, might as well be this one
      onUpdate() {
        const coords = from.clone().addScaledVector(diff, progress.x)
        const normalized = uvToWorld(coords, camera.position).normalize() // will be copied into camera.position too
        const newPos = uvOpposite(worldToUv(normalized))
        camera.position.multiplyScalar(CAMERA_DISTANCE_TO_TARGET)
        onUpdate(newPos) // call onUpdate with the current UV coords
      },
      onComplete: completeOnlyOnce,
    })
  }

  let fovTween: gsap.core.Tween | null = null

  if (fov) {
    fovTween = gsap.to(camera, {
      duration,
      fov: fov.x,
      onUpdate: () => {
        camera.updateProjectionMatrix()
        const newPos = uvOpposite(worldToUv(camera.position.clone().normalize()))
        onUpdate(newPos)
      },
      onComplete: completeOnlyOnce,
    })
  }

  return () => {
    fovTween?.kill()
    positionTween?.kill()
    completeOnlyOnce()
  }
}

export { tweenCamera }
